import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  changeStakeholderStatus,
  createStakeholder,
  deleteStakeholder,
  getStakeholders,
  reorderStakeholders,
  updateStakeholder,
} from '../api/stakeholderApi'
import type { PagedResponse, Stakeholder, StakeholderStatus, StakeholderUpsert } from '../types'

const KEY = 'activity-stakeholders'

/**
 * Paydaş/katılımcı yönetimi sunucu-durumu (022): liste + oluştur/güncelle/durum/sil mutation'ları. Her
 * başarıda liste invalidate edilir. Güncel kayıt cache'e yazılır (bayat modDate → 409 önlenir).
 * (012 useRejectReasons deseniyle aynı; ret-lookup invalidate'i yok — tüketen akış henüz kapsam dışı.)
 */
export function useStakeholders() {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [KEY] })
  }

  const applyUpdated = (updated: Stakeholder) =>
    qc.setQueryData<PagedResponse<Stakeholder>>([KEY, i18n.language], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((t) => (t.id === updated.id ? updated : t)) }
        : prev,
    )

  const list = useQuery({
    queryKey: [KEY, i18n.language],
    queryFn: getStakeholders,
  })

  // meta.handledCodes: domaine özel iş kodu ekranda ele alınır → merkezi hata toast'ı (019) susar.
  const create = useMutation({
    mutationFn: (body: StakeholderUpsert) => createStakeholder(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: StakeholderUpsert }) =>
      updateStakeholder(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: StakeholderStatus }) =>
      changeStakeholderStatus(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteStakeholder(id),
    onSuccess: invalidate,
  })

  // Yeniden sıralama: liste anında (optimistic) yeni sıraya geçer; backend 1..N yazar. Hata → geri al.
  const reorder = useMutation({
    mutationFn: (orderedIds: number[]) => reorderStakeholders(orderedIds),
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: [KEY, i18n.language] })
      const prev = qc.getQueryData<PagedResponse<Stakeholder>>([KEY, i18n.language])
      if (prev) {
        const byId = new Map(prev.content.map((r) => [r.id, r]))
        const content = orderedIds
          .map((id) => byId.get(id))
          .filter((r): r is Stakeholder => r != null)
        qc.setQueryData<PagedResponse<Stakeholder>>([KEY, i18n.language], { ...prev, content })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData([KEY, i18n.language], ctx.prev)
    },
    onSettled: invalidate,
  })

  return { list, create, update, changeStatus, remove, reorder }
}
